import {
  IOrganizationBankInfo,
  IOrganizationSavedAddress,
} from "../../../setup/api/services/organization/models";
import {
  formatIban,
  normalizeIban,
} from "../organizationRegistration.constants";
import { IAddressDraft, IBankAccountDraft } from "./types";

/** e.g. "SA44 8000 0000 0123 4567 8912" — the task summary and the pending-screen row. */
export const bankSummary = (bank: IBankAccountDraft) => formatIban(bank.iban);

/** e.g. "RRRD1234 · الرياض، الملقا" */
export const addressSummary = (address: IAddressDraft) =>
  `${address.nationalAddress} · ${[address.city.name, address.district.name]
    .filter(Boolean)
    .join("، ")}`;

/** The org's saved bank account (from my-organization) as a task draft. */
export const bankDraftFromSaved = (
  bank?: Partial<IOrganizationBankInfo>,
  registry: { commercialRegisterNumber?: string; unifiedNumber?: string } = {}
): IBankAccountDraft | null => {
  if (!bank?.iban) return null;
  const iban = normalizeIban(bank.iban);
  return {
    iban,
    beneficiaryName: bank.beneficiaryName ?? "",
    commercialRegisterNumber: registry.commercialRegisterNumber ?? "",
    unifiedNumber: registry.unifiedNumber ?? "",
  };
};

/**
 * The org's saved address (from my-organization) as a task draft. The saved address carries
 * names only, no province/city/district ids, so those options get id 0 — the address modal
 * sends the user back through the national-address lookup before they can edit it.
 */
export const addressDraftFromSaved = (
  address?: IOrganizationSavedAddress
): IAddressDraft | null => {
  if (!address?.nationalAddress) return null;
  return {
    nationalAddress: address.nationalAddress,
    province: { id: 0, name: address.state ?? "" },
    city: { id: 0, name: address.city ?? "" },
    district: { id: 0, name: address.district ?? "" },
    street: address.street ?? "",
    postalCode: address.postalCode ?? "",
    buildingNo: address.buildingNo ?? "",
  };
};

/** True for a draft built by addressDraftFromSaved, which can't fill the dropdowns. */
export const isSavedAddressDraft = (address: IAddressDraft) =>
  !address.province.id || !address.city.id || !address.district.id;
